import { apiClient } from './api.client';

/**
 * Analytics Service
 * API client for workspace analytics endpoints (admin-only)
 */

export interface MessageVolumeData {
  timestamp: string;
  count: number;
}

export interface MessageVolumeSummary {
  totalMessages: number;
  averagePerPeriod: number;
  peakTime: string;
  peakCount: number;
}

export interface ActiveUser {
  userId: string;
  displayName: string;
  messageCount: number;
  lastActive: string | null;
}

export interface ActiveChannel {
  channelId: string;
  name: string;
  messageCount: number;
  uniqueUsers: number;
}

export interface ChannelResponseTime {
  channelId: string;
  name: string;
  avgResponseTime: number; // seconds
}

/**
 * Get message volume over time
 */
export const getMessageVolume = async (params: {
  period: 'hourly' | 'daily' | 'weekly' | 'monthly';
  startDate: string;
  endDate: string;
  channelId?: string;
  userId?: string;
  includeDMs?: boolean;
}): Promise<{
  analytics: MessageVolumeData[];
  summary: MessageVolumeSummary;
}> => {
  const response = await apiClient.get('/analytics/messages/volume', {
    params,
  });
  return response.data.data;
};

/**
 * Get most active users
 */
export const getActiveUsers = async (params: {
  period: '7d' | '30d' | '90d' | 'all';
  limit?: number;
}): Promise<{
  activeUsers: ActiveUser[];
  totalActiveUsers: number;
  totalInactiveUsers: number;
}> => {
  const response = await apiClient.get('/analytics/users/active', {
    params,
  });
  return response.data.data;
};

/**
 * Get most active channels
 */
export const getActiveChannels = async (params: {
  period: '7d' | '30d' | '90d' | 'all';
  limit?: number;
}): Promise<{
  activeChannels: ActiveChannel[];
}> => {
  const response = await apiClient.get('/analytics/channels/active', {
    params,
  });
  return response.data.data;
};

/**
 * Get response time metrics
 */
export const getResponseTime = async (params: {
  period: '7d' | '30d' | '90d' | 'all';
  channelId?: string;
}): Promise<{
  globalAverage: number;
  channelAverages: ChannelResponseTime[];
}> => {
  const response = await apiClient.get('/analytics/response-time', {
    params,
  });
  return response.data.data;
};

/**
 * Export analytics data
 */
export const exportAnalytics = async (params: {
  format: 'csv' | 'json';
  metrics: 'all' | 'messages' | 'users' | 'channels' | 'response-time';
  startDate: string;
  endDate: string;
}): Promise<Blob> => {
  const response = await apiClient.get('/analytics/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Trigger download of exported analytics file
 */
export const downloadAnalytics = async (params: {
  format: 'csv' | 'json';
  metrics: 'all' | 'messages' | 'users' | 'channels' | 'response-time';
  startDate: string;
  endDate: string;
}): Promise<void> => {
  const blob = await exportAnalytics(params);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `analytics-${Date.now()}.${params.format}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
